import type {
    ApplicationWorkbookTemplateListItem,
    OrgApplicationWorkbookListItem,
    PropertyApplicationWorkbookListItem,
} from './workbook.types'

export function formatWorkbookTemplateProvenance(
    item: Pick<
        PropertyApplicationWorkbookListItem | OrgApplicationWorkbookListItem,
        'created_from_template_key' | 'created_from_template_version'
    >,
    templatesByKey?: Map<string, ApplicationWorkbookTemplateListItem>,
): string | null {
    const key = item.created_from_template_key
    const version = item.created_from_template_version
    if (!key || version == null) {
        return null
    }

    const templateName = templatesByKey?.get(key)?.name ?? key
    return `From ${templateName} v${version}`
}

export function formatWorkbookOrgProvenance(
    workbook: Pick<
        PropertyApplicationWorkbookListItem,
        'created_from_org_workbook_id'
    >,
    orgWorkbooksById?: Map<number, OrgApplicationWorkbookListItem>,
): string | null {
    const orgWorkbookId = workbook.created_from_org_workbook_id
    if (!orgWorkbookId) {
        return null
    }

    const orgName =
        orgWorkbooksById?.get(orgWorkbookId)?.name ??
        `Org workbook ${orgWorkbookId}`
    return `From ${orgName}`
}

export function formatPropertyWorkbookProvenance(
    workbook: Pick<
        PropertyApplicationWorkbookListItem,
        | 'created_from_template_key'
        | 'created_from_template_version'
        | 'created_from_org_workbook_id'
        | 'created_from_season_id'
        | 'created_from_season_name'
        | 'copied_from_name'
        | 'adoption_status'
    >,
    options?: {
        templatesByKey?: Map<string, ApplicationWorkbookTemplateListItem>
        orgWorkbooksById?: Map<number, OrgApplicationWorkbookListItem>
    },
): string | null {
    if (workbook.created_from_season_id && workbook.created_from_season_name) {
        return `Copied from ${workbook.created_from_season_name}`
    }

    if (workbook.copied_from_name) {
        return `Copy of ${workbook.copied_from_name}`
    }

    const orgProvenance = formatWorkbookOrgProvenance(
        workbook,
        options?.orgWorkbooksById,
    )
    if (orgProvenance) {
        return orgProvenance
    }

    return formatWorkbookTemplateProvenance(workbook, options?.templatesByKey)
}

export function formatPropertyWorkbookMenuLabel(
    workbook: PropertyApplicationWorkbookListItem,
    options?: {
        templatesByKey?: Map<string, ApplicationWorkbookTemplateListItem>
        orgWorkbooksById?: Map<number, OrgApplicationWorkbookListItem>
    },
): string {
    const provenance = formatPropertyWorkbookProvenance(workbook, options)
    return provenance ? `${workbook.name} — ${provenance}` : workbook.name
}
